import { useQuery } from '@tanstack/react-query';

import type {
  ExtractedTableJson,
  FieldSuggestion,
  PartBotResultJson,
  QueryFunction,
  SimilarPartResult,
} from '@frontend/api';
import { useRequest } from '@frontend/api/src/common';

export type QuoteHistoryResult = {
  count: number;
  next: null;
  previous: null;
  results: {
    account?: string;
    componentId: string;
    contact?: string;
    id: number;
    quoteNumberWithRevision: string;
    quoteItemId: number;
    quoteStatus: string;
    sentDate: null;
  }[];
};

export type JobHistoryResult = {
  count: number;
  next: string | null;
  previous: string | null;
  results: [];
};

export type QuoteItemOverviewBundleData = {
  partBotResults: PartBotResultJson[];
  fieldSuggestions: FieldSuggestion[];
  similarParts: SimilarPartResult[];
  extractedTables: ExtractedTableJson[];
  quoteHistory: QuoteHistoryResult;
  jobHistory: JobHistoryResult;
};

type UseGetQuoteItemOverviewBundleParams = {
  quoteItemId: number;
};

export type QuoteItemOverviewBundleQueryKey = readonly [
  'quoteItemOverviewBundle',
  number,
];

export const getQuoteItemOverviewBundleQueryKey = ({
  quoteItemId,
}: UseGetQuoteItemOverviewBundleParams): QuoteItemOverviewBundleQueryKey => [
  'quoteItemOverviewBundle',
  quoteItemId,
];

export const useGetQuoteItemOverviewBundle: QueryFunction<
  QuoteItemOverviewBundleData,
  [UseGetQuoteItemOverviewBundleParams],
  QuoteItemOverviewBundleQueryKey
> = ({ quoteItemId }, { requestOptions, queryOptions } = {}) => {
  const request = useRequest(requestOptions);
  const queryKey = getQuoteItemOverviewBundleQueryKey({ quoteItemId });

  return useQuery({
    ...queryOptions,
    queryKey,
    queryFn: ({ signal }) =>
      request<QuoteItemOverviewBundleData>(
        `/quotes/items/${quoteItemId}/overview_bundle/`,
        {
          init: {
            method: 'GET',
            signal,
          },
        },
      ),
  });
};
